"use client";

import React from "react";

import { revalidateUserPortfolioPaths } from "@/app/actions/portfolio/portfolio-actions";
import { Button } from "@/components/ui/button";
import { Intervals } from "@/constants";
import {
  JobProgressProps,
  useJobProgressReader,
} from "@/hooks/job/progress/use-read-job-progress-reader";
import { useJobsStore } from "@/stores/jobs-store";
import { JobPayloadSchema } from "@/utils/job/progress/job-progress-utils";
import { FilePieChartIcon } from "lucide-react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { z } from "zod";
import { useShallow } from "zustand/shallow";

function useDefaultMessageHandler() {
  const router = useRouter();

  const defaultMessageHandler = (
    jobId: string,
    message: z.infer<typeof JobPayloadSchema>,
  ) => {
    const jobMeta = useJobsStore.getState().jobs.get(jobId);

    if (message?.status === "failed") {
      toast.error(`${jobMeta?.name ?? ""} failed: ${message.payload.message}`, {
        duration: Intervals.SECOND * 10,
      });
    }

    if (message?.status === "completed") {
      const toastId = toast(
        <div className="flex w-full grow flex-col gap-3">
          <div className="flex flex-col items-center gap-2 md:flex-row">
            <FilePieChartIcon className="size-6" />
            <p>{jobMeta?.name ?? ""} completed.</p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={async () => {
              await revalidateUserPortfolioPaths();
              toast.dismiss(toastId);
              router.refresh();
            }}
          >
            Refresh
          </Button>
        </div>,
        {
          duration: Intervals.SECOND * 10,
        },
      );
    }
  };

  return defaultMessageHandler;
}

export function JobProgress({ jobId }: JobProgressProps) {
  const { data: session } = useSession();
  const jobMeta = useJobsStore(useShallow((state) => state.jobs.get(jobId)));
  const defaultMessageHandler = useDefaultMessageHandler();
  const readStream = useJobProgressReader(
    jobId,
    JobPayloadSchema,
    defaultMessageHandler,
  );

  React.useEffect(() => {
    if (!jobId || !session?.user.id) {
      return;
    }

    const abortController = new AbortController();
    readStream(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [jobId, readStream, session?.user.id]);

  React.useEffect(() => {
    if (!jobMeta) {
      return;
    }

    toast.info(jobMeta.name);
  }, [jobMeta]);

  return null;
}
